import React, { useCallback, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { expVal } from '@confluence/feature-experiments';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';

import { WidgetConnectorExperienceTrackerSupport } from './WidgetConnectorExperienceTrackerSupport.ts';

import { i18n } from './widgetMacroI18n.ts';
import type {
	EmbedDescriptor,
	ProviderMatcher,
	SanitisedParams,
	WidgetMacroParams,
} from './widgetMacroTypes.ts';
import { EmptyState } from './components/EmptyState.tsx';
import { DeprecatedEmbed } from './components/embeds/DeprecatedEmbed.tsx';
import { ErrorEmbed } from './components/embeds/ErrorEmbed.tsx';
import { FullscreenIframeEmbed } from './components/embeds/FullscreenIframeEmbed.tsx';
import { IframeEmbed } from './components/embeds/IframeEmbed.tsx';
import { TwitterWidgetEmbed } from './components/embeds/TwitterWidgetEmbed.tsx';
import { YoutubeIframeEmbed } from './components/embeds/YoutubeIframeEmbed.tsx';
import { matchDailyMotion } from './matchers/dailymotionMatcher.ts';
import { matchFigma } from './matchers/figmaMatcher.ts';
import { matchFlickr } from './matchers/flickrMatcher.ts';
import { matchGoogleCalendar } from './matchers/googleCalendarMatcher.ts';
import { matchGoogleDocs } from './matchers/googleDocsMatcher.ts';
import { matchGoogleMaps } from './matchers/googleMapsMatcher.ts';
import { matchGoogleSpreadsheets } from './matchers/googleSpreadsheetsMatcher.ts';
import { matchMicrosoftStream } from './matchers/microsoftStreamMatcher.ts';
import { matchMyspace } from './matchers/myspaceMatcher.ts';
import { matchScribd } from './matchers/scribdMatcher.ts';
import { matchTudou } from './matchers/tudouMatcher.ts';
import { matchTwitter } from './matchers/twitterMatcher.ts';
import { matchVimeo } from './matchers/vimeoMatcher.ts';
import { matchWufoo } from './matchers/wufooMatcher.ts';
import { matchYoutube } from './matchers/youtubeMatcher.ts';
import { getHost, isBlockedDomain } from './utils/urlMatcherUtils.ts';

/**
 * Provider matchers in dispatch priority order. Mirrors the registration order
 * of `WidgetRenderer` beans in the legacy `atlassian-plugin.xml` so that for any
 * URL the same provider that historically claimed it continues to do so.
 */
const PROVIDER_MATCHERS: readonly ProviderMatcher[] = [
	matchYoutube,
	matchVimeo,
	matchDailyMotion,
	matchMicrosoftStream,
	matchMyspace,
	matchTudou,
	matchFlickr,
	matchFigma,
	matchGoogleDocs,
	matchGoogleSpreadsheets,
	matchGoogleCalendar,
	matchGoogleMaps,
	matchScribd,
	matchWufoo,
	matchTwitter,
];

/**
 * Resolves a widget URL to an `EmbedDescriptor` describing how to render it.
 *
 * Returns an `error` descriptor if:
 *  - the URL targets a blocked Atlassian internal-service domain (SSRF guard), or
 *  - no provider matcher claims the URL, or
 *  - the matched provider returned a falsy embed URL.
 *
 * This function is pure & SSR-safe.
 */
export function dispatchEmbed(url: string, params: SanitisedParams): EmbedDescriptor {
	const host = getHost(url);

	if (isBlockedDomain(url)) {
		return {
			kind: 'error',
			provider: 'unknown',
			embedUrl: url,
			width: '',
			height: '',
			hostLabel: host,
		};
	}

	for (const matcher of PROVIDER_MATCHERS) {
		const result = matcher(url, params);
		if (result && result.embedUrl) {
			return result;
		}
	}

	return {
		kind: 'error',
		provider: 'unknown',
		embedUrl: url,
		width: '',
		height: '',
		hostLabel: host,
	};
}

/**
 * Strips parameters that the macro never trusts from a consumer (CONFCLOUD-65632 /
 * CONFSERVER-57916). The legacy macro removed the internal `template` parameter; we
 * simply ignore it by returning a typed `SanitisedParams` containing only allow-listed
 * fields.
 */
export function sanitiseParams(input: {
	width?: { value?: string };
	height?: { value?: string };
}): SanitisedParams {
	return {
		width: input.width?.value || undefined,
		height: input.height?.value || undefined,
	};
}

const renderErrorFallback = () => (
	<FormattedMessage {...i18n.unsupportedProvider} values={{ url: '' }} />
);

export type WidgetMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: WidgetMacroParams;
	/**
	 * Optional fallback URL extracted from the macro body by the upstream
	 * extension handler. Mirrors the legacy WidgetMacro behaviour where an empty
	 * `url` parameter falls back to the trimmed macro body.
	 */
	macroBody?: string;
};

type EmbedViewProps = {
	descriptor: EmbedDescriptor;
};

/**
 * Stateless presentation component that renders the appropriate DOM for the
 * supplied `EmbedDescriptor`. Each `kind` is handled by a dedicated subcomponent
 * in `./components/embeds/`. This component is SSR-safe (no `window`/`document`
 * access at module scope and no effects required for the iframe variants).
 */
const EmbedView = ({ descriptor }: EmbedViewProps) => {
	switch (descriptor.kind) {
		case 'iframe':
			return <IframeEmbed descriptor={descriptor} />;
		case 'fullscreen-iframe':
			return <FullscreenIframeEmbed descriptor={descriptor} />;
		case 'youtube-iframe':
			return <YoutubeIframeEmbed descriptor={descriptor} />;
		case 'twitter-widget':
			return <TwitterWidgetEmbed descriptor={descriptor} />;
		case 'deprecated':
			return <DeprecatedEmbed descriptor={descriptor} />;
		case 'error':
		default:
			return <ErrorEmbed descriptor={descriptor} />;
	}
};

/**
 * Tracks the WIDGET_CONNECTOR_EMBED_EXPERIENCE for the "Widget connector embed"
 * capability (Group 6 of the @ecosystem Feature Test Migration), gated behind the
 * `confluence_enable_connect_group_6_metrics` experiment.
 *
 * Unlike Connect iframes, widget embeds resolve synchronously — the outcome is
 * known as soon as the URL is dispatched to a provider matcher — so the
 * experience starts and resolves within a single effect. Renders nothing; mirrors
 * the canonical singleton-backed pattern with start/succeed/fail + guard refs.
 */
const WidgetConnectorEmbedExperience = ({
	rawUrl,
	hostLabel,
	descriptorError,
}: {
	rawUrl: string;
	hostLabel?: string | null;
	descriptorError: Error | null;
}) => {
	const experienceStartedRef = useRef(false);
	const experienceResolvedRef = useRef(false);

	// Note: this component is only rendered when the
	// `confluence_enable_connect_group_6_metrics` flag is on (gated at the call
	// site in WidgetMacroRendererInner), so no flag check is needed here.
	const startExperienceIfNeeded = useCallback(() => {
		const extension = { url: rawUrl, hostLabel };

		if (!WidgetConnectorExperienceTrackerSupport.isTrackable(extension)) {
			return false;
		}

		if (!experienceStartedRef.current) {
			WidgetConnectorExperienceTrackerSupport.start(extension);
			experienceStartedRef.current = true;
		}

		return true;
	}, [rawUrl, hostLabel]);

	useEffect(() => {
		if (!startExperienceIfNeeded() || experienceResolvedRef.current) {
			return;
		}

		if (descriptorError) {
			WidgetConnectorExperienceTrackerSupport.fail(descriptorError);
		} else {
			WidgetConnectorExperienceTrackerSupport.succeed();
		}
		experienceResolvedRef.current = true;
	}, [startExperienceIfNeeded, descriptorError]);

	// Abort if the component unmounts before the render experience resolves.
	useEffect(() => {
		return () => {
			if (experienceStartedRef.current && !experienceResolvedRef.current) {
				WidgetConnectorExperienceTrackerSupport.abort(
					'WidgetMacroRenderer unmounted before widget embed experience resolved',
				);
			}
		};
	}, []);

	return null;
};

const WidgetMacroRendererInner = ({
	macroDefaultProps,
	macroParams,
	macroBody,
}: WidgetMacroRendererProps) => {
	const { contentId, extensionKey, mode, node } = macroDefaultProps;
	const attributes = macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(mode, node);
	const macroId = macroDefaultProps?.attributes?.macroId;
	const rawUrl = macroParams?.url?.value?.trim() || macroBody?.trim() || '';

	if (!rawUrl) {
		return (
			<>
				<MacroExperienceSuccess
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
				/>
				<EmptyState />
			</>
		);
	}

	const sanitised = sanitiseParams({
		width: macroParams?.width,
		height: macroParams?.height,
	});
	const descriptor = dispatchEmbed(rawUrl, sanitised);
	const descriptorError =
		descriptor.kind === 'error'
			? new Error(`Unsupported widget provider for ${descriptor.hostLabel || rawUrl}`)
			: null;

	return (
		<>
			{descriptorError ? (
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
					error={descriptorError}
				/>
			) : (
				<MacroExperienceSuccess
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={mode}
					attributes={attributes}
				/>
			)}
			{expVal('confluence_enable_connect_group_6_metrics', 'isEnabled', false) && (
				<WidgetConnectorEmbedExperience
					rawUrl={rawUrl}
					hostLabel={descriptor.hostLabel}
					descriptorError={descriptorError}
				/>
			)}
			<Box testId="widget-macro" data-fabric-macro={macroId} data-macro-body="">
				<EmbedView descriptor={descriptor} />
			</Box>
		</>
	);
};

/**
 * React rewrite of the legacy `confluence-widget-connector` macro. Dispatches a
 * supplied URL to one of the per-provider matchers in `./matchers/` and renders
 * the appropriate embed (iframe, fullscreen iframe, Twitter placeholder) or an
 * error fallback.
 *
 * Wrapped in {@link GenericErrorBoundary} so that any provider matcher
 * regression cannot crash the host page.
 */
export const WidgetMacroRenderer = (props: WidgetMacroRendererProps): React.JSX.Element => (
	<GenericErrorBoundary
		renderOnError={renderErrorFallback}
		attribution={Attribution.PAGE_EXPERIENCES}
	>
		<WidgetMacroRendererInner {...props} />
	</GenericErrorBoundary>
);
